import { Component, OnInit, Injector } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { UploadComponent, FileInfo, FileState } from '@progress/kendo-angular-upload';
import { GridDataResult } from '@progress/kendo-angular-grid';

import { DataGridTemplate, flatten } from '../../../shared/data-grid/data-grid.template';
import { FormDialogTemplate } from '../../../shared/form-dialog/form-dialog.template';
import { ToolingComponent } from './tooling-components/tooling.component';
import { cgpAuthentication } from '../../../../modules/cgp-authentication';
import { PartSearchComponent } from './part-search/part-search.component';

import { FilterableService } from '../../mesc/filterable-service';
import { RequestService } from './request.service';
import { S3Service } from '../../../shared/services/s3.service';
import { RequestStatus } from '../workflow.service';
import { AuthenticationService } from '../../../auth.service';
import {
    CtwrPartService,
    CtwrLocationApproverService,
    CtwrAuthorizationTypeService,
    CtwrRequestService
} from '../ctwr.service';
import { CgpConfirmationDialogService } from '../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';

import { RequestTemplate } from './request.template';
import { takeUntil } from 'rxjs/operators';

const DETAIL_ROUTE = '/Detail?requestId=';


@Component({
    selector: 'app-request',
    templateUrl: './request.component.html',
    styleUrls: ['./request.component.scss']
})
export class RequestComponent extends DataGridTemplate implements OnInit {
    private isPteTm: boolean;
    private isDesigner: boolean;
    private permanentId: string;
    private formBuilder: FormBuilder;
    private confirmationDialog: CgpConfirmationDialogService;
    private router: Router;
    private s3Service: S3Service;
    private partService: CtwrPartService;
    private ctwrRequestService: CtwrRequestService;
    private authService: AuthenticationService;
    private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
    readonly toolingInvalidText: Subject<string> = new BehaviorSubject<string>('');
    readonly impactedParts: FilterableService = new FilterableService();
    readonly primaryParts: FilterableService = new FilterableService();
    readonly primaryPart: FilterableService = new FilterableService();
    readonly temporaryRequests: FilterableService = new FilterableService();
    readonly filteredTemporaryRequests: FilterableService = new FilterableService();


    private readonly disabledControls = {
        add: () => !this.isPteTm,
        inbox: () => !this.isPteTm && !this.isDesigner,
        default: () => false
    }

    columns: any[];
    editForm: FormGroup;
    formDialog: FormDialogTemplate;
    uploadFiles: any[] = [];
    isRequestViewAll: boolean;
    isSaving: boolean;
    toolingComponent: ToolingComponent;
    partSearchComponent: PartSearchComponent;
    locationApproverService: CtwrLocationApproverService;
    requestService: RequestService;
    authorizationTypeService: CtwrAuthorizationTypeService;

    constructor(private readonly injector: Injector) { super(); }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    ngOnInit() {
        this.initialize();
        this.registerServices();
        this.isRequestViewAll = this.requestService.getViewState();
    }


    /**
    * Initialize Form Dialog and Data Grid
    */
    private initialize() {
        this.initializeComponents();
        const _roles = this.authService.getUserRoles();
        this.isPteTm = _roles.some(r => (r.endsWith('TM') || r.endsWith('PTE')));
        this.isDesigner = _roles.some(r => r.endsWith('DES'));
        this.formDialog = new FormDialogTemplate();
        this.formDialog.setHeight(720);
        this.formDialog.largeForm = true;
        this.formDialog.save = this.onSave;
        this.formDialog.cancel = this.closeDialog;
        this.formDialog.controls.submit = true;
        this.pageable.pageSizes = [10, 25, 50, 100];
        this.modal = true;
        this.filterable = 'menu';
        this.controls.width = 135;
        this.controls.customToolBarButtons = RequestTemplate.customToolbarButtons(this);
        this.controls.configure = true;
        this.controls.edit = false;
        this.controls.delete = false;
        this.reorderable = true;
        this.resizable = true;
        this.columns = RequestTemplate.columns(this, this.requestService);
        this.editForm = this.getFormGroup();
        this.formDialog.initilize("Request", this.editForm, RequestTemplate.formDialogTabs(this));
        this.initializeServices();

        if (this.disabledControls['inbox']()) {
            this.isRequestViewAll = true;
            this.requestService.setViewState(true);
        }
    }


    /**
     * 
     */
    private initializeComponents() {
        this.formBuilder = this.injector.get(FormBuilder);
        this.confirmationDialog = this.injector.get(CgpConfirmationDialogService);
        this.toolingComponent = this.injector.get(ToolingComponent);
        this.partSearchComponent = this.injector.get(PartSearchComponent);
        this.locationApproverService = this.injector.get(CtwrLocationApproverService);
        this.router = this.injector.get(Router);
        this.s3Service = this.injector.get(S3Service);
        this.partService = this.injector.get(CtwrPartService);
        this.requestService = this.injector.get(RequestService);
        this.ctwrRequestService = this.injector.get(CtwrRequestService);
        this.authorizationTypeService = this.injector.get(CtwrAuthorizationTypeService);
        this.authService = this.injector.get(AuthenticationService);
    }


    /**
     * Query and populate services
     */
    private initializeServices() {

        this.authorizationTypeService.read()
            .then(x => {
                if (!x) return;
                const match = x.find(i => i.Name === 'Permanent');
                if (match) this.permanentId = match.Id;
                return x;
            })
        this.partService.read()
            .then(x => {
                this.primaryParts.next(x);
            });
    }


    /**
     * 
     */
    private registerServices() {
        this.partSearchComponent.selectedPart
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => {
                this.primaryPartChange(x ? x.PartId : x);
                this.updatePrimaryPartDetails(x)
            });

        this.impactedParts
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.editForm.get('ImpactedPartIds').setValue(x.map(i => i.Id)));

        this.toolingComponent.editForm
            .valueChanges
            .subscribe(x => {
                if (x && x.ComponentCategories) {
                    this.editForm.get("RequestToolingComponents").setValue(x.ComponentCategories);
                    this.editForm.get('toolingBtn').setValue(this.toolingComponent.editForm.valid);
                }
                return x;
            });

        this.requestService.isRequestViewAll
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(x => this.isRequestViewAll = x);
    }


    /**
     * 
     */
    private updateTemporaryRequests() {
        const authType = this.authorizationTypeService.value.find(x => x.Id !== this.permanentId);
        if (authType) {
            const params = new HttpParams()
                .set('AuthorizationTypeId', authType.Id)
                .toString();
            this.ctwrRequestService.read(`/TemporaryRequests?${params.toString()}`)
                .then(r => {
                    r.map(res => {
                        res.Name = `${String(res.RequestNumber)} - ${res.Subject}`;
                        return res;
                    })
                    this.filteredTemporaryRequests.next(r);
                });
        }
    }


    /**
     * Build a FormGroup for the Request
     */
    private readonly getFormGroup = (): FormGroup => this.formBuilder.group({
        PrimaryPartId: ['', Validators.required],
        ProcessId: ['', Validators.required],
        AuthorizationTypeId: ['', Validators.required],
        Subject: ['', Validators.compose([Validators.required, Validators.maxLength(65)])],
        Detail: ['', Validators.compose([Validators.required, Validators.maxLength(1000)])],
        ImpactedPartIds: [],
        RequestToolingComponents: [],
        RequestAttachmentDtos: [],
        RequestLocationApproverDtos: [],
        CreatedBy: [],
        PartDetails: [],
        LocationApprovers: [],
        RequestId: [],
        RequestNumber: [],
        TemporaryRequestIds: [],
        RequestStatusDesc: [],
        toolingBtn: [false, Validators.requiredTrue],
        IsSimpleRequest: [false],
        EstimatedGrossSavings: [0, Validators.compose([Validators.required, Validators.max(999999999.99)])],
        EstimatedCostofChange: [0, Validators.compose([Validators.required, Validators.max(999999999.99)])],
        NetSavings: [],
        DesignCA: [false, [this.validateRequestDetails]],
        EngineeringWorkOrder: [undefined, [this.validateRequestDetails]],
        PlantProcessChangeRequest: [undefined, [this.validateRequestDetails]],
        ToolChangeRequest: [undefined, [this.validateRequestDetails]],
    });


    /****************************************************************************
     * GRID CALLBACKS
     ***************************************************************************/


    read(opts: any): Promise<any> {
        this.isServerSideFiltering = this.isRequestViewAll;
        return this.isRequestViewAll ?
            this.handleSearch(opts) :
            this.getInbox(opts);
    }

    handleExport = () => {
        const _path = '/Search';
        const d = new Date()
        const _fileType = '.xlsx';
        const _fileName = [
            'CTWR', 'Requests', d.getDate(), (d.getMonth() + 1), d.getFullYear()]
            .join('_') + _fileType;
        const _params = this.getSearchParams(this.gridState, true);

        this.ctwrRequestService
            .export(this.getFilters(true), _path, _params)
            .then(response => {
                const nav = (window.navigator as any)
                const blob = new Blob([response], { type: "application/vnd.ms-excel" });
                if (typeof navigator !== "undefined" && nav.msSaveOrOpenBlob) {
                    nav.msSaveOrOpenBlob(blob, _fileName);
                }
                else {
                    const a = window.document.createElement("a");
                    a.href = window.URL.createObjectURL(blob);
                    a.download = _fileName;
                    document.body.appendChild(a)
                    a.click();
                }
            })
    }

    /**
     * 
     * @param opts 
     */
    private handleSearch(opts: any): Promise<any> {
        const filters = this.getFilters();
        const options = this.getSearchParams();

        return this.ctwrRequestService.search(filters, '/Search', true, options)
            .then(x => {
                if (!x) return { Requests: [], ReportLookupData: {} };
                x.Requests = this.requestService.mapRequestDates(x.Requests);
                this.requestService.setLookupData(x.ReportLookupData);
                return {
                    data: x.Requests,
                    total: x.RecordCount
                } as GridDataResult
            });
    }


    /**
     * 
     * @param opts 
     */
    private getInbox(opts: any): Promise<any> {
        return opts.force ?
            this.ctwrRequestService.read("/RequestInbox")
                .then(x => {
                    if (!x) return { Requests: [], ReportLookupData: {} };
                    x.Requests = this.requestService.mapRequestDates(x.Requests);
                    this.requestService.setLookupData(x.ReportLookupData);
                    this.updateFilters();
                    return x.Requests;
                }) : Promise.resolve(this.ctwrRequestService.value.Requests);
    }


    create(parentEditDataItem: any): void {
        const userInfo = cgpAuthentication.getUserInfo();
        if (userInfo) this.editDataItem.CreatedBy = userInfo.cn;
        this.editForm.removeControl('deleteBtn');
        this.uploadFiles = [];
        this.editDataItem.AuthorizationTypeId = this.permanentId;
        this.formDialog.isNew = true;
        this.editDataItem.EstimatedGrossSavings = 0;
        this.editDataItem.EstimatedCostofChange = 0;
        this.formDialog.next(this.editDataItem);
        this.requestService.openDialog(this.formDialog);
        this.toolingComponent.editForm.removeControl('PartNumber');
        this.filteredTemporaryRequests.next([]);
    }


    edit(dataItem: any): void {
        this.ctwrRequestService.read(DETAIL_ROUTE + dataItem.RequestId)
            .then(x => {
                if (!x) return;
                x.RequestAttachmentDtos = this.mapRequestAttachmentDto(x.RequestAttachmentDtos);
                this.uploadFiles = x.RequestAttachmentDtos ? x.RequestAttachmentDtos : [];
                x.RequestStatusDesc = x.RequestStatus;
                this.editDataItem = x;
                this.primaryPartChange(x.PrimaryPartId, true);
                this.authorizationTypeChange(x.AuthorizationTypeId);
                if (this.editDataItem.RequestStatus === RequestStatus.INITIAL) {
                    this.editForm.addControl('deleteBtn', new FormControl());
                }
                this.formDialog.isNew = false;
                this.formDialog.next(x);
                this.handleDesignCAChange(false);
                this.requestService.openDialog(this.formDialog);
                if (this.toolingComponent.editForm.get('PartNumber')) {
                    this.toolingComponent.editForm.removeControl('PartNumber')
                };
            })
    }


    save(dataItem: any, isNew: boolean): Promise<any> {
        this.closeDialog();
        this.isSaving = true;
        dataItem = this.cleanUpRequestData(dataItem);
        const toolingComponents = dataItem.RequestToolingComponents;
        this.editForm.reset({});
        let requestNumber: string = dataItem.RequestNumber;
        if (!isNew && requestNumber.toLowerCase().startsWith('wr')) {
            requestNumber = requestNumber.substr(2);
            dataItem.RequestNumber = requestNumber;
        }

        if (!dataItem.IsSimpleRequest) dataItem.IsSimpleRequest = false;

        const _path = isNew ? "?strAction=Save" : `?requestId=${dataItem.RequestId}`;
        return this.ctwrRequestService.save(dataItem, isNew, _path, false)
            .then(response => {
                this.editDataItem = response;
                this.editDataItem.RequestToolingComponents = toolingComponents;
                return this.handleS3Upload(response)
                    .then(_ => {
                        this.isSaving = false;
                    })
                    .catch(e => {
                        if (e.status !== 200) this.deleteAllRequestAttachments(response);
                        this.s3Service.handleS3Error(e);
                        this.isSaving = false;
                    });
            });
    }


    submit = (dataItem: any, isNew: boolean): Promise<any> => {
        if (!dataItem.EstimatedGrossSavings) {
            dataItem.EstimatedGrossSavings = 0;
        }
        if (!dataItem.EstimatedCostofChange) {
            dataItem.EstimatedCostofChange = 0;
        }
        return this.save(dataItem, isNew)
            .then(response => {
                return this.ctwrRequestService.read(`/Detail/ByRequestNumber?requestNumber=${this.editDataItem.RequestNumber}&isRefresh=false`)
                    .then(x => {
                        x.ImpactedPartIds = dataItem.ImpactedPartIds;
                        x.RequestToolingComponents = this.editDataItem.RequestToolingComponents;
                        x.RequestNumber = x.RequestNumber.substr(2);
                        this.ctwrRequestService.save(x, true, "?strAction=Submit", false)
                            .then(r => this.refresh());
                    });
            });
    }


    delete(dataItem: any = this.editDataItem): Promise<any> {
        this.confirmationDialog.showConfirmationDialog({
            title: 'Delete Confirmation',
            message: `Are you sure that you want to delete Request# ${dataItem.RequestNumber}?`,
            alert: 'danger',
            onYesClick: () => this.onDelete(dataItem),
            onNoClick: () => this.confirmationDialog.closeConfirmationDialog()
        });
        return null;
    }

    onDelete(dataItem) {
        this.confirmationDialog.closeConfirmationDialog();
        return this.ctwrRequestService.read(DETAIL_ROUTE + dataItem.RequestId)
            .then(x => {
                x.RequestAttachmentDtos = this.mapRequestAttachmentDto(x.RequestAttachmentDtos);
                if (x.RequestAttachmentDtos) {
                    this.deleteAllS3Attachments(x);
                }
                return this.ctwrRequestService.remove(`?id=${dataItem.RequestId}`, false)
                    .then(x => {
                        this.refresh();
                        this.formDialog.next(null);
                    });
            });
    }


    deleteAllS3Attachments(dataItem: any) {
        let requestNumber = dataItem.RequestNumber;
        if (!requestNumber.toLowerCase().startsWith('wr')) {
            requestNumber = `WR${requestNumber}`;
        }
        const path = `CTWR/${requestNumber}/`
        dataItem.RequestAttachmentDtos.forEach(f => {
            this.s3Service.handleS3Remove(f, path + f.name);
        });
    }


    /**
     * Use router to navigate to Detail view for selected Request
     * If record is editable, launch the edit dialog instead
     * @param button - selected grid button
     * @param dataItem - json for selected row (Request)
     */
    onViewDetail = (button: any, dataItem: any) => {
        if (dataItem.RequestStatus === RequestStatus.INITIAL ||
            !this.isRequestViewAll && (
                dataItem.RequestStatus === RequestStatus.REWORK_REQUESTED ||
                dataItem.RequestStatus === RequestStatus.REWORK_IN_PROGRESS
            )
        ) {
            this.onEdit(dataItem, -1);
        } else {
            this.router.navigate(['/CTWR/Detail', dataItem.RequestNumber]);
        }
    }


    /**
     * Compare grid state filters to lookup data and remove filters that do not exist
     * in the lookup data
     */
    private readonly updateFilters = () => {
        const filters = this.getFilters();
        Object.keys(filters).forEach(x => {
            const lookupData = this.requestService.lookupData[x];
            if (lookupData && lookupData.value) {
                if (!filters[x].some(f => lookupData.value.some(d => d === f))) {
                    this.gridState.filter.filters = this.gridState.filter.filters
                        .filter(f1 => f1.filters.some(f2 => f2.field !== x));
                    if (this.gridState.filter.filters.length < 1) {
                        delete this.gridState.filter;
                    }
                }
            }
        });
    }


    /**
     * 
     */
    toggleRequestView = (isRequestViewAll: boolean = false) => {
        if (this.isRequestViewAll === isRequestViewAll || this.disabledControls['inbox']()) return;
        this.gridState.skip = 0;
        this.requestService.setViewState(isRequestViewAll);
        this.refresh();
    }


    /**
     * 
     */
    isDisabled = (id: string, dataItem: any): boolean => {
        return (this.disabledControls[id] || this.disabledControls['default'])();
    }


    /****************************************************************************
     * DIALOG ACTIONS
     ***************************************************************************/

    /**
     * 
     */
    closeDialog = () => {
        this.editDataItem = undefined;
        this.formDialog.next(this.editDataItem);
        this.requestService.closeDialog();
        this.impactedParts.next([]);
        this.primaryPartChange(undefined);
        this.locationApproverService.next([]);
    }


    /**
     * 
     */
    primaryPartChange = (id: string, isEdit = false) => {
        const toolingBtn = this.editForm.get('toolingBtn');
        const tempRequestIds = this.editForm.get('TemporaryRequestIds');
        tempRequestIds.disable();
        toolingBtn.disable();
        tempRequestIds.setValue([]);

        const part = this.primaryParts.value.find(x => x.Id === id);
        this.setImpactedParts(id, isEdit);
        if (part) {
            if (!this.editDataItem) return;
            this.editForm.get('ProcessId').setValue(part.ProcessId);
            if (isEdit) part.RequestId = this.editDataItem.RequestId;
            this.updatePrimaryPartDetails(part);
            if (this.editForm.value.AuthorizationTypeId === this.permanentId) tempRequestIds.enable();
            this.filteredTemporaryRequests.next([]);
            const authType = this.editDataItem.AuthorizationTypeId;
            if (authType === this.permanentId) this.updateTemporaryRequests();
            this.toolingComponent.setPartDetail(part)
                .then(x => toolingBtn.enable());
            this.toolingInvalidText.next('*You must select at least 1 Item from the Tooling Component List.');
            this.primaryPart.next([part]);

        } else {
            this.toolingInvalidText.next('*Select a Primary Part');
            this.updatePrimaryPartDetails();
            this.primaryPart.next([]);
        }
    }


    /**
     * 
     */
    authorizationTypeChange = (id: string) => {
        const ctrl = this.editForm.get('TemporaryRequestIds');
        ctrl.setValue([]);
        ctrl.disable();
        if (id === this.permanentId && this.editForm.get('PrimaryPartId').value) {
            ctrl.enable();
            this.updateTemporaryRequests();
        }
    };


    /**
     * 
     */
    handleDesignCAChange = (clear = true) => {
        const ctrl = this.editForm.get('DesignCA');
        this.resetControl('EngineeringWorkOrder', !ctrl.value, clear);
        this.resetControl('PlantProcessChangeRequest', !ctrl.value, clear);
        this.resetControl('ToolChangeRequest', !ctrl.value, clear);
    }

    private resetControl(key: string, isEnabled: boolean, clear: boolean) {
        const ctrl = this.editForm.get(key);
        if (clear) ctrl.setValue(null);
        ctrl.markAsUntouched();
        isEnabled ? ctrl.enable() : ctrl.disable();
    }

    readonly isEnabled = (id: string, dataItem: any): boolean => {
        let enabled = true;
        if (id === 'export') {
            enabled = this.isRequestViewAll;
        } else if (id === 'excel') {
            enabled = !this.isRequestViewAll;
        }
        return enabled;
    }

    /****************************************************************************
     * REQUEST METHODS
     ***************************************************************************/

    cleanUpRequestData = (dataItem: any): any => {
        delete dataItem.CreatedBy;
        delete dataItem.toolingBtn;
        delete dataItem.deleteBtn;
        delete dataItem.PartDetails;
        delete dataItem.LocationApprovers;
        if (this.formDialog.isNew) delete dataItem.RequestId;
        if (!dataItem.IsSimpleRequest) dataItem.IsSimpleRequest = false;

        const toolingComponents = dataItem.RequestToolingComponents;
        if (dataItem.DesignCA === null) dataItem.DesignCA = false;
        if (toolingComponents && toolingComponents.ComponentCategories) {
            dataItem.RequestToolingComponents = dataItem.RequestToolingComponents.ComponentCategories;
        }

        dataItem.RequestAttachmentDtos = this.uploadFiles.map(f => {
            return {
                FileName: f.name,
                FileDescription: f.comment,
                Path: f.name,
                state: f.state
            }
        });
        return dataItem;
    }


    mapRequestAttachmentDto(files: any[]): FileInfo[] {
        return files ? files
            .map(i => {
                return {
                    name: i.FileName,
                    CreatedBy: i.CreatedBy,
                    CreatedDate: i.CreatedDate,
                    uid: i.Id,
                    state: FileState.Uploaded,
                    comment: i.FileDescription
                } as FileInfo;
            }) : null;
    }


    /**
     * Remove attachments for request
     */
    deleteAllRequestAttachments(dataItem: any) {
        if (!dataItem) return;
        const _path = '/RequestAttachment?requestAttachmentIds=';
        const files: any[] = dataItem.RequestAttachmentDtos;

        if (files) {
            files.forEach(f => {
                const id = f.Id;
                const isNew = this.uploadFiles.some(x => x.name === f.FileName && x.state < 3);
                if (id && isNew) this.ctwrRequestService.remove(`${_path}${id}&RequestId=${dataItem.RequestId}`);
            })
        } else {
            this.ctwrRequestService.read(`/Detail/ByRequestNumber?requestNumber=${dataItem.RequestNumber}&isRefresh=false`)
                .then(x => this.deleteAllRequestAttachments(x));
        }
    }


    /**
     * For each file selected, add file to list for files to upload
     */
    handleSelect = (data: any, form: FormGroup, c: UploadComponent) => {
        const files: any[] = data.files;
        if (files.length !== undefined) {
            data.files.forEach(f => {
                const name: string = f.name;
                const fileParts = name.split('.');
                const type = `.${fileParts.pop()}`;
                f.name = fileParts.join('.').replace(/[\W_]+/g, "_").replace('_amp', '') + type;
                this.uploadFiles.push(f)
            });
            this.editForm.get('RequestAttachmentDtos').setValue(this.uploadFiles);
        }
    }


    handleCommentCancel = (file: any, c: UploadComponent) => {
        const index = this.uploadFiles.indexOf(file);
        this.uploadFiles.splice(index, 1);
        this.editForm.get('RequestAttachmentDtos').setValue(this.uploadFiles);
    }


    handleCommentSave = (files: any[], form: FormGroup, c: UploadComponent) => {
        if (files.length !== undefined) {
            files.forEach(f => f.state = FileState.Initial);
        }
    }


    /**
     * Upload control remove callback
     * If file has been uploaded, remove from S3 and request record
     * Remove file from upload control
     * Remove file from uploadFiles list
     */
    handleRemove = (file: FileInfo, form: FormGroup, c: UploadComponent) => {
        if (file.state === FileState.Uploaded) {
            const requestId = this.editDataItem.RequestId;
            const path = `/RequestAttachment?requestAttachmentIds=${file.uid}`;

            this.handleS3Remove(file)
                .then(x => {
                    this.ctwrRequestService.remove(`${path}&RequestId=${requestId}`);
                })
                .catch(e => this.s3Service.handleS3Error(e));
        }

        c.fileList.remove(file.uid);
        this.uploadFiles = this.uploadFiles
            .filter(x => file.uid !== x.uid);
    }


    /**
     * 
     * @param file 
     * @param request 
     */
    handleS3Remove(file, request: any = this.editDataItem): Promise<any> {
        const _path = `CTWR/${request.RequestNumber}/`;
        return this.s3Service.handleS3Remove(file, _path)
    }


    /**
     * 
     * @param request 
     */
    handleS3Upload(request: any = this.editDataItem): Promise<any> {
        const _path = `CTWR/${request.RequestNumber}/`;
        return this.s3Service.handleS3Upload(this.uploadFiles, _path, request.RequestId);
    }


    /**
     * 
     * @param filePath: CTWR/{RequestNumber}/{FileName}
     */
    getS3Object = (file: FileInfo, form: FormGroup) => {
        if (!this.editForm.value.RequestNumber) return;
        const _path = `CTWR/${this.editForm.value.RequestNumber}/`;
        this.s3Service.downloadS3Object(file, _path);
    }


    private updatePrimaryPartDetails(part: any = null) {
        const ctrl = this.editForm.get('PartDetails');
        if (part) {
            ctrl.setValue([
                {
                    value: part.ProgramName,
                    label: 'Program',
                },
                {
                    value: part.Location,
                    label: 'Location',
                },
                {
                    value: part.PartType,
                    label: 'Part Type',
                },
                {
                    value: part.ProcessName,
                    label: 'Process',
                },
                {
                    value: part.PartFamily,
                    label: 'Part Family',
                },
            ]);
            this.editForm.get('PrimaryPartId').setValue(part.Id ? part.Id : part.PartId);
        } else {
            ctrl.setValue([]);
        }
    }


    /**
     * 
     */
    private readonly setImpactedParts = (id, isEdit: boolean) => {
        if (id) {
            let _path = `/impactedparts/${id}`;
            if (isEdit) _path = `${_path}?requestId=${this.editDataItem.RequestId}`;
            this.partService.read(_path)
                .then(x => {
                    if (!x) return;
                    const impactedPartIds: any[] = x.map(p => p.Id);
                    this.impactedParts.next(x);
                    this.setLocationApprovers(id, impactedPartIds);
                })
        } else {
            this.impactedParts.next([]);
            this.locationApproverService.next([]);
        }
    }


    private setLocationApprovers(id: string, impactedPartIds: any[]) {
        let query = `?partIds=${id}`;
        impactedPartIds.forEach(x => query += `&partIds=${x}`);
        this.locationApproverService.read(`/GetByParts${query}`)
            .then(x => this.editForm.get("RequestLocationApproverDtos").setValue(x));
    }


    /**
     * Construct HttpParams using the gridState 
     */
    private getSearchParams(state: any = this.gridState, isExport = false): HttpParams {
        let _sort: any = state.sort[0];
        if (!_sort || !_sort.dir) _sort = { dir: 'desc', field: 'RequestNumber' };
        let _pageNumber = (state.skip / state.take) + 1;
        if (isNaN(_pageNumber)) _pageNumber = 1;
        let httpParams: HttpParams = new HttpParams()
            .set('orderBy', _sort.field)
            .set('sortOrder', _sort.dir)
            .set('export', isExport.toString());

        if (!this.isAll) {
            httpParams = httpParams
                .set('pageSize', String(state.take))
                .set('pageNumber', _pageNumber.toString())
        }

        return httpParams;
    }


    /**
     * Map and return filters from gridState
     */
    private getFilters(isExport = false) {
        const _filters: any = {};
        const gridFilter = this.gridState.filter;

        flatten(gridFilter).map(f => {
            const _field: string = f.field;
            let _values = f.value.toString().split(',').map(x => x.trim());
            if (_field === 'Subject' || _field === 'Detail') {
                _values = _values.join();
            }
            if (_filters[_field]) {
                _values.forEach(v => _filters[_field].push(v));
            } else if (_field.toLowerCase().includes('date') && f.operator) {
                const datePrefix = f.operator === 'gte' ? 'From' : 'To';
                _filters[`${datePrefix}${f.field}`] = f.value;
            } else {
                _filters[_field] = _values;
            }
        });
        return _filters;
    }


    /**
     * 
     */
    private readonly validateRequestDetails = (control: AbstractControl): ValidationErrors => {
        if (!this.editForm) return { empty: null };
        const controls: AbstractControl[] = [
            this.editForm.get('DesignCA'),
            this.editForm.get('EngineeringWorkOrder'),
            this.editForm.get('PlantProcessChangeRequest'),
            this.editForm.get('ToolChangeRequest')
        ];

        const isControlValid = this.isValidInput(control.value);
        const valid: boolean = isControlValid || controls.some(c => this.isValidInput(c.value));
        const response: ValidationErrors = valid ? null : { valid: false };
        controls.forEach(c => c.setErrors(response));
        return response
    }


    /**
     * Return true if value is defined and not empty
     * Return false otherwise
     */
    private readonly isValidInput = (value): boolean => value && value !== "";


    /**
     * 
     */
    openToolingComponent = () => {
        this.toolingComponent.isSimpleRequest = this.editForm.value.IsSimpleRequest;
        this.toolingComponent.open();
    }


    readonly handleSimpleRequestChange = () => {
        this.toolingComponent.isSimpleRequest = this.editForm.value.IsSimpleRequest;
        this.toolingComponent.validateForm(false);
        this.toolingComponent.isSimpleRequest = true;
    }
}
